import {
  Fab,
  FormControl,
  Grid,
  IconButton,
  makeStyles,
  MenuItem,
  Select,
  Typography,
  TextField,
  Card,
  CardContent,
  CardMedia,
  RadioGroup,
  FormControlLabel,
  Radio,
} from "@material-ui/core";
import _ from "lodash";
import { TestDriveBookingStepper } from "../TestDriveStepper";
import { Form, Formik } from "formik";
import ArrowBackIcon from "@material-ui/icons/ArrowBack";
import { useDispatch, useSelector } from "react-redux";
import { IAppState } from "../../../../../store";
import { updateTestDriveBooking } from "../../../../../actions/testDriveBooking/actions";
import {
  DeliveryType,
  IDriverSlot,
  TestDriveBookingSteps,
  testDriveExperienceOptions,
  TestDriveExperiences,
  TestDriveTypes,
  testDriveTypesOptions,
} from "../../../../../reducers/testDriveBooking/types";
import ArrowForwardIcon from "@material-ui/icons/ArrowForward";
import { useEffect, useState } from "react";
import { ApolloError, useLazyQuery } from "@apollo/client";
import { GET_VEHICLE_AVAILABILITY } from "../../../../../graphql/bookings/getTestDriveVehicleAvailability";
import { SnackBarVariant } from "../../../../SnackbarWrapper/SnackbarWrapper";
import {
  formatGraphQLErrorMessage,
  getVehicleTypeIcon,
} from "../../../../utils";
import { DateTime as d } from "luxon";
import { useSnackBar } from "../../../../SnackBarContext/SnackBarContext";
import { CalendarTimeSlotSelector } from "./SlotPicker";
import { Skeleton } from "@material-ui/lab";
import { debounce } from "lodash";
import { getLocalizedBookingSyntex } from "../../../../../utils/localized.syntex";
import AddressPicker from "../../../../AddressPicker/AddressPicker";
import { BookingType, IAddress } from "../../../../../reducers/bookings/types";
import * as Yup from "yup";
import { getSignedUrl } from "../../../../../utils/getSignedUrl";
import { useTranslation } from "react-i18next";

const useStyles = makeStyles((theme) => ({
  imageBox: {
    width: "95%",
    height: "75%",
    backgroundSize: "cover",
    backgroundPosition: "center",
  },
  title: {
    marginTop: theme.spacing(0.5),
    marginBottom: theme.spacing(2),
  },
  formControl: {
    minWidth: 250,
  },
  select: {
    "& .MuiOutlinedInput-notchedOutline": {
      borderRadius: 0,
    },
    "& .MuiOutlinedInput-input": { cursor: "pointer" },
  },
  text: { marginTop: theme.spacing(2), marginBottom: theme.spacing(0.5) },
  cardTitle: {
    paddingLeft: 0,
  },
  proceedIcon: {
    marginRight: theme.spacing(1),
  },
  proceedButton: {
    marginTop: theme.spacing(2),
    marginBottom: theme.spacing(0.5),
    borderRadius: 0,
  },
  cardRoot: {
    minHeight: 500,
  },
  cardMedia: {
    minWidth: "600px",
    minHeight: "300px",
    borderRadius: 6,
    objectFit: "contain",
  },
}));

interface IVehicleSlotSelection {
  testDriveType: TestDriveTypes;
  testDriveExperience: TestDriveExperiences;
  selectedPickupSlot: string;
  selectedDropoffSlot: string;
  selectedDate: string;
  vehicleId: string;
  calendarPickupInitialSlot: number;
  calendarDropoffInitialSlot: number;
  extendedTestDriveDuration: number;
  pickupSlot?: IDriverSlot;
  dropoffSlot?: IDriverSlot;
  pickupOtherLocation: IAddress;
  deliveryType: DeliveryType;
}

export interface IVehicleAvailability {
  timeSlot: string;
  slotId: string;
  driverId: string;
}

const VehicleDetailsCard = ({ imageUrl = "", title = "" }) => {
  const classes = useStyles();
  const [image, setImage] = useState<string>(getVehicleTypeIcon("COUPE"));
  const getImage = async (image: string) => {
    const file = await getSignedUrl(image).then((result) => {
      return result;
    });
    const imgResponse = await fetch(file as string).then((res) => {
      return res;
    });
    if (!imgResponse.ok) {
      return;
    }
    setImage(file as string);
  };

  useEffect(() => {
    if (imageUrl) {
      getImage(imageUrl);
    }
  });

  return (
    <Card className={classes.cardRoot} elevation={0}>
      <CardMedia
        image={image}
        component="img"
        className={classes.cardMedia}
        title={title}
      />
      <CardContent>
        <Typography variant="h2" component="h2" className={classes.cardTitle}>
          {title}
        </Typography>
      </CardContent>
    </Card>
  );
};

const initialVehicleSelection: IVehicleSlotSelection = {
  testDriveType: TestDriveTypes.STANDARD,
  testDriveExperience: TestDriveExperiences.CONSULTATION,
  vehicleId: "",
  selectedDate: d.now().toUTC().toISO(),
  extendedTestDriveDuration: 1,
  pickupOtherLocation: {
    street: "",
    city: "",
    fullAddress: "",
    state: "",
    country: "",
    zipcode: "",
    geometry: {
      type: "",
      latitude: 0,
      longitude: 0,
    },
  },
  selectedPickupSlot: "",
  selectedDropoffSlot: "",
  calendarPickupInitialSlot: 0,
  calendarDropoffInitialSlot: 0,
  deliveryType: DeliveryType.DEALERSHIP,
};

export const VehicleConfirmation: React.FC = () => {
  const classes = useStyles();
  const dispatch = useDispatch();
  const snackbar = useSnackBar();
  const [vehicleSlotSelection, setVehicleSelection] =
    useState<IVehicleSlotSelection>(initialVehicleSelection);
  const [openSlotPicker, setSlotPickerState] = useState<boolean>(false);
  const [vehicleAvailability, setVehicleAvailability] = useState<
    IVehicleAvailability[]
  >([]);
  const [isDriverAvailableForDropOff, setIsDriverAvailableForDropOff] =
    useState<boolean>(false);
  const [isDriverAvailableForPickup, setIsDriverAvailableForPickUp] =
    useState<boolean>(false);
  const [vehicleAvailabilityDropOffSlots, setVehicleAvailabilityDropOffSlots] =
    useState<IVehicleAvailability[]>([]);
  const [vehicleAvailabilityPickUpSlots, setVehicleAvailabilityPickUpSlots] =
    useState<IVehicleAvailability[]>([]);

  const transformAddress = (address: any) => {
    if (!address) return undefined;
    let geometry = address.geometry;
    if (!geometry && address.location) {
      geometry = {
        type: address.location.type || "",
        latitude: address.location.latitude || 0,
        longitude: address.location.longitude || 0
      };
    }
    return {
      street: address.street || "",
      city: address.city || "",
      fullAddress: address.fullAddress || "",
      state: address.state || "",
      country: address.country || "",
      zipcode: address.zipcode || "",
      geometry: geometry || {
        type: "",
        latitude: 0,
        longitude: 0
      }
    };
  };

  const testDriveBookingState = useSelector(
    (state: IAppState) => state.testDriveBookingReducer
  );
  const [slot, setSlot] = useState<string>("");
  const [updatedPickupDate, setPickupDate] = useState<string>("");
  const [updatedDropoffDate, setDropoffDate] = useState<string>("");
  const appSetting = useSelector(
    (state: IAppState) => state.consumerWebsiteReducer.consumerWebsite
  );
  const currentBranch = appSetting.branches.find(
    (branch) => branch.id === testDriveBookingState.branchId
  );
  const organisation = useSelector(
    (state: IAppState) =>
      state.consumerWebsiteReducer.consumerWebsite.organisation
  );
  const extendedTestDriveMaxHours = organisation.extendedTestDriveMaxHours;
  const standardTestDriveDuration = organisation.standardTestDriveDuration;
  const country =
    useSelector(
      (state: IAppState) =>
        state.consumerWebsiteReducer.consumerWebsite.organisation
    ).address.country || "";
  const enableExtendedTestDrive = organisation.enableExtendedTestDrive;
  const filteredTestDriveTypes = testDriveTypesOptions.filter(
    (option) =>
      option.value === TestDriveTypes.STANDARD || enableExtendedTestDrive
  );
  const isExtendedTestDrive =
    vehicleSlotSelection.testDriveType === TestDriveTypes.EXTENDED;
  const { t } = useTranslation();
  const [getTestDriveAvailablilty, { loading: availabilityLoading }] =
    useLazyQuery(GET_VEHICLE_AVAILABILITY, {
      fetchPolicy: "network-only",
      onCompleted: (data) => {
        if (data.getTestDriveVehicleAvailability) {
          const { isExtended, dropoffSlots, pickupSlots } =
            data.getTestDriveVehicleAvailability;
          if (
            isExtended &&
            (dropoffSlots?.timeSlots.length || pickupSlots?.timeSlots.length)
          ) {
            setIsDriverAvailableForDropOff(!!dropoffSlots?.driverSlotsExist);
            setIsDriverAvailableForPickUp(!!pickupSlots?.driverSlotsExist);

            setVehicleAvailabilityDropOffSlots(dropoffSlots?.timeSlots || []);
            setVehicleAvailabilityPickUpSlots(pickupSlots?.timeSlots || []);
            const selectedDate = d.fromISO(vehicleSlotSelection.selectedDate);
            const pickupTimeSlot =
              pickupSlots?.timeSlots?.[0]?.timeSlot || t("root:na");
            const dropoffTimeSlot =
              dropoffSlots?.timeSlots?.[0]?.timeSlot || t("root:na");

            setSlot(
              `${selectedDate.toLocaleString(
                d.DATE_FULL
              )}  | ${dropoffTimeSlot}`
            );

            const newPickupTime = d
              .fromISO(vehicleSlotSelection.selectedDate, {
                zone: currentBranch?.timeZone,
              })
              .startOf("day")
              .plus({
                hours: d.fromFormat(
                  dropoffSlots?.timeSlots?.[0]?.timeSlot,
                  "h:mm a"
                ).hour,
              });
            const newDropoffTime = newPickupTime.plus({
              hours:
                isExtendedTestDrive && extendedTestDriveMaxHours
                  ? extendedTestDriveMaxHours
                  : testDriveBookingState?.extendedTestDriveDuration || 1,
            });
            setPickupDate(newPickupTime.toUTC().toISO());
            setDropoffDate(newDropoffTime.toUTC().toISO());
          } else {
            setIsDriverAvailableForDropOff(
              !!dropoffSlots?.driverSlotsExist &&
                dropoffSlots?.timeSlots?.length
            );
            setVehicleAvailabilityDropOffSlots(dropoffSlots?.timeSlots || []);
            const selectedDate = d.fromISO(vehicleSlotSelection.selectedDate);
            const dropoffTimeSlot =
              dropoffSlots?.timeSlots?.[0]?.timeSlot || t("root:na");
            const slotExists = dropoffSlots?.timeSlots?.[0]?.timeSlot;
            const newPickupTime = d
              .fromISO(vehicleSlotSelection.selectedDate, {
                zone: currentBranch?.timeZone,
              })
              .startOf("day")
              .plus({
                hours: slotExists ? d.fromFormat(slotExists, "h:mm a").hour : 0,
              });
            let newDropoffTime = newPickupTime;
            if (isExtendedTestDrive && extendedTestDriveMaxHours) {
              newDropoffTime = newDropoffTime.plus({
                hours: extendedTestDriveMaxHours,
              });
            } else if (
              standardTestDriveDuration &&
              standardTestDriveDuration.active
            ) {
              newDropoffTime = newDropoffTime.plus({
                hours: standardTestDriveDuration.hour,
              });
            } else {
              newDropoffTime = newPickupTime.plus({
                hours: vehicleSlotSelection.extendedTestDriveDuration || 1,
              });
            }
            if (slot.length && isExtended) {
              setSlot(slot);
            } else {
              setSlot(
                `${selectedDate.toLocaleString(
                  d.DATE_FULL
                )} | ${dropoffTimeSlot}`
              );
            }
            setPickupDate(newPickupTime.toUTC().toISO());
            setDropoffDate(newDropoffTime.toUTC().toISO());
          }
        }
      },
      onError: (error: ApolloError) => {
        snackbar({
          message: formatGraphQLErrorMessage(error.message),
          variant: SnackBarVariant.ERROR,
        });
      },
    });

  useEffect(() => {
    if (
      testDriveBookingState.availableVehicleIds &&
      appSetting.id &&
      testDriveBookingState.branchId
    ) {
      getTestDriveAvailablilty({
        variables: {
          availabilityParams: {
            dropoffQueryDate: d.now().plus({ day: 1 }).toUTC().toISO(),
            pickupQueryDate: d.now().toUTC().toISO(),
            vehicleIds: testDriveBookingState.availableVehicleIds,
            branchId: testDriveBookingState.branchId,
            category: BookingType.TEST_DRIVE,
            pickupServiceLocation: testDriveBookingState.pickupLocation,
            dropoffOtherLocation: transformAddress(testDriveBookingState.pickupOtherLocation),
            extendedTestDriveDuration:
              vehicleSlotSelection.extendedTestDriveDuration,
          },
        },
      });
    }
  }, [testDriveBookingState, getTestDriveAvailablilty, appSetting]);

  const nextStep = (testDriveValues: IVehicleSlotSelection) => {
    const testDriveTypeString = testDriveTypesOptions.filter(
      (types) => types.value === testDriveValues.testDriveType
    )[0].label;
    dispatch(
      updateTestDriveBooking({
        ...testDriveBookingState,
        ...testDriveValues,
        deliveryType: testDriveBookingState.deliveryType,
        stepperData: {
          ...testDriveBookingState.stepperData,
          slot: slot,
          experience: testDriveTypeString,
        },
        currentStep: TestDriveBookingSteps.PERSONAL_DETAILS,
        testDriveBookingDetails: {
          ...testDriveBookingState.testDriveBookingDetails,
          type: testDriveValues.testDriveType,
        },
        pickupSlot: testDriveValues.pickupSlot,
        dropoffSlot: testDriveValues.dropoffSlot,
        pickupDate: updatedPickupDate,
        dropoffDate: updatedDropoffDate,
      })
    );
  };

  const prevStep = () => {
    dispatch(
      updateTestDriveBooking({
        ...testDriveBookingState,
        currentStep: TestDriveBookingSteps.VEHICLE_SELECTION,
      })
    );
  };

  const handleSlotPickerClose = () => {
    setSlotPickerState(false);
  };

  const delayedFetchAvailability = debounce(
    (extendedTestDriveDuration: number) => {
      getTestDriveAvailablilty({
        variables: {
          availabilityParams: {
            dropoffQueryDate: d.now().plus({ day: 1 }).toUTC().toISO(),
            pickupQueryDate: d.now().toUTC().toISO(),
            vehicleIds: testDriveBookingState.availableVehicleIds,
            branchId: testDriveBookingState.branchId,
            category: BookingType.TEST_DRIVE,
            pickupServiceLocation: testDriveBookingState.pickupLocation,
            dropoffOtherLocation: transformAddress(testDriveBookingState.pickupOtherLocation),
            extendedTestDriveDuration: extendedTestDriveDuration,
          },
        },
      });
    },
    500
  );

  const schema = Yup.object().shape({
    pickupOtherLocation: Yup.mixed().when("deliveryType", {
      is: DeliveryType.HOME,
      then: Yup.object()
        .shape({
          fullAddress: Yup.string().required(t("validation:addressRequired")),
          country: Yup.string().required(
            t("validation:registeredAddressCountryRequired")
          ),
          state: Yup.string().required(
            t("validation:registeredAddressStateRequired")
          ),
          city: Yup.string().required(
            t("validation:tradingAddressCityRequired")
          ),
          street: Yup.string().required(
            t("validation:tradingAddressStreetRequired")
          ),
        })
        .required(t("validation:addressRequired")),
      otherwise: Yup.object().notRequired(),
    }),
  });

  return (
    <Grid item container spacing={2}>
      <Grid
        item
        container
        xs={12}
        spacing={1}
        alignItems="flex-start"
        justify="flex-start"
      >
        <Grid item>
          <IconButton aria-label="previous-step" onClick={prevStep}>
            <ArrowBackIcon />
          </IconButton>
        </Grid>
        <Grid item xs={10}>
          <Typography variant="h1" component={"h1"}>
            {t("root:selectASlot")}
          </Typography>
        </Grid>
      </Grid>

      <Grid item xs={12}>
        <TestDriveBookingStepper />
      </Grid>
      <Grid item container xs={12} spacing={2}>
        <Grid item container xs={12} sm={12} md={7} lg={7} xl={7}>
          <VehicleDetailsCard
            title={testDriveBookingState.stepperData.model}
            imageUrl={testDriveBookingState.vehicleImage}
          />
        </Grid>
        <Grid
          item
          container
          xs={12}
          sm={12}
          md={5}
          lg={5}
          xl={5}
          alignContent="flex-start"
          alignItems="flex-start"
        >
          <Grid item xs={12}>
            <Typography variant="h2" component={"h2"}>
              {t("root:bookATestDrive")}
            </Typography>
          </Grid>

          <Grid item container xs={12}>
            <Formik
              validationSchema={schema}
              enableReinitialize
              initialValues={vehicleSlotSelection}
              onSubmit={(values, { setSubmitting }) => {
                const updatedVehicleSlotSelection = {
                  ...vehicleSlotSelection,
                  pickupOtherLocation: {
                    ...vehicleSlotSelection.pickupOtherLocation,
                  },
                };

                nextStep(updatedVehicleSlotSelection);
                setSubmitting(false);
              }}
            >
              {(formikProps) => (
                <Form>
                  <Grid item xs={12}>
                    <FormControl
                      variant="outlined"
                      className={classes.formControl}
                    >
                      <Typography variant="body1" className={classes.text}>
                        {getLocalizedBookingSyntex(country)} {t("root:type")}
                      </Typography>
                      <Select
                        displayEmpty
                        value={vehicleSlotSelection.testDriveType}
                        onChange={(event) => {
                          delayedFetchAvailability(
                            parseInt(event.target.value as string)
                          );
                          setVehicleSelection({
                            ...vehicleSlotSelection,
                            extendedTestDriveDuration: 1,
                            testDriveType: event.target.value as TestDriveTypes,
                          });
                        }}
                        name="testDriveType"
                        className={classes.select}
                      >
                        {filteredTestDriveTypes.map((option) => (
                          <MenuItem key={option.value} value={option.value}>
                            {option.label}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>
                  <Grid item xs={12}>
                    <FormControl
                      variant="outlined"
                      className={classes.formControl}
                    >
                      <Typography variant="body1" className={classes.text}>
                        {t("root:experience")}
                      </Typography>
                      <Select
                        id={`-select`}
                        displayEmpty
                        value={formikProps.values.testDriveExperience}
                        onChange={formikProps.handleChange}
                        className={classes.select}
                      >
                        {testDriveExperienceOptions.map((option) => (
                          <MenuItem key={option.value} value={option.value}>
                            {option.label}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>
                  <Grid item xs={12}>
                    <FormControl>
                      <Typography variant="body1" className={classes.text}>
                        {t("root:deliveryType")}
                      </Typography>
                      <RadioGroup
                        row
                        aria-labelledby="delivery-type-radio-group-label"
                        name="deliveryType"
                        value={vehicleSlotSelection.deliveryType}
                        onChange={(e) => {
                          const deliveryType = e.target.value as DeliveryType;
                          setVehicleSelection({
                            ...vehicleSlotSelection,
                            deliveryType: deliveryType,
                          });
                          dispatch(
                            updateTestDriveBooking({
                              ...testDriveBookingState,
                              deliveryType: deliveryType,
                            })
                          );
                        }}
                      >
                        <FormControlLabel
                          value={DeliveryType.DEALERSHIP}
                          control={<Radio />}
                          label={t("root:atDealership")}
                        />
                        <FormControlLabel
                          value={DeliveryType.HOME}
                          control={<Radio />}
                          label={t("root:atHome")}
                        />
                      </RadioGroup>
                    </FormControl>
                  </Grid>
                  {formikProps.values.deliveryType === DeliveryType.HOME && (
                    <Grid item xs={12}>
                      <FormControl
                        variant="outlined"
                        className={classes.formControl}
                      >
                        <Typography variant="body1" className={classes.text}>
                          {t("root:deliveryAddress")} *
                        </Typography>
                        <AddressPicker
                          fieldName={"pickupOtherLocation"}
                          addGeometry={true}
                          onChange={(address: IAddress) => {
                            if (address && address.fullAddress) {
                              setVehicleSelection({
                                ...vehicleSlotSelection,
                                pickupOtherLocation: address,
                              });
                              formikProps.setFieldValue(
                                "pickupOtherLocation",
                                address
                              );
                              getTestDriveAvailablilty({
                                variables: {
                                  availabilityParams: {
                                    dropoffQueryDate: d
                                      .now()
                                      .plus({ day: 1 })
                                      .toUTC()
                                      .toISO(),
                                    pickupQueryDate: d.now().toUTC().toISO(),
                                    vehicleIds:
                                      testDriveBookingState.availableVehicleIds,
                                    branchId: testDriveBookingState.branchId,
                                    category: BookingType.TEST_DRIVE,
                                    pickupServiceLocation:
                                      testDriveBookingState.pickupLocation,
                                    dropoffOtherLocation: address,
                                    extendedTestDriveDuration:
                                      vehicleSlotSelection.extendedTestDriveDuration,
                                  },
                                },
                              });
                            } else {
                              setVehicleSelection({
                                ...vehicleSlotSelection,
                                pickupOtherLocation: {
                                  street: "",
                                  city: "",
                                  country: "",
                                  fullAddress: "",
                                  state: "",
                                  zipcode: "",
                                  geometry: {
                                    type: "",
                                    latitude: 0,
                                    longitude: 0,
                                  },
                                },
                              });
                              formikProps.setFieldValue(
                                "pickupOtherLocation",
                                initialVehicleSelection.pickupOtherLocation
                              );
                            }
                          }}
                        />
                        {formikProps.errors.pickupOtherLocation && (
                          <Typography
                            variant="body2"
                            color="error"
                            style={{ marginTop: 8 }}
                          >
                            {Object.values(
                              formikProps.errors.pickupOtherLocation
                            ).find(Boolean)}
                          </Typography>
                        )}
                      </FormControl>
                    </Grid>
                  )}
                  <Grid item xs={12}>
                    <FormControl
                      variant="outlined"
                      className={classes.formControl}
                    >
                      <Typography variant="body1" className={classes.text}>
                        {t("root:selectSlot")}
                      </Typography>
                      {availabilityLoading ? (
                        <Skeleton variant="rect" animation="wave" height={40} />
                      ) : vehicleSlotSelection.selectedDate &&
                        vehicleAvailabilityDropOffSlots.length ? (
                        <TextField
                          id={`-select`}
                          value={slot}
                          onClick={() => setSlotPickerState(true)}
                          className={classes.select}
                          InputProps={{
                            readOnly: true,
                            style: { cursor: "pointer" },
                          }}
                        ></TextField>
                      ) : (
                        <TextField
                          id={`-select`}
                          value={`Vehicle Not Available on Selected Date`}
                          onClick={() => setSlotPickerState(true)}
                          className={classes.select}
                          InputProps={{
                            readOnly: true,
                            style: { cursor: "pointer" },
                          }}
                        ></TextField>
                      )}
                    </FormControl>
                  </Grid>
                  <Grid item xs={12}>
                    <FormControl
                      variant="outlined"
                      className={classes.formControl}
                    >
                      <Grid item xs={12}>
                        <Fab
                          aria-label="proceed-with-vehicle-selection"
                          variant="extended"
                          size="medium"
                          type="submit"
                          style={{
                            backgroundColor:
                              formikProps.isValid &&
                              (formikProps.values.deliveryType !==
                                DeliveryType.HOME ||
                                (formikProps.values.deliveryType ===
                                  DeliveryType.HOME &&
                                  formikProps.values.pickupOtherLocation
                                    ?.fullAddress))
                                ? "var(--primary-color)"
                                : "#e0e0e0",
                          }}
                          className={classes.proceedButton}
                          disabled={
                            !formikProps.isValid ||
                            (formikProps.values.deliveryType ===
                              DeliveryType.HOME &&
                              !formikProps.values.pickupOtherLocation
                                ?.fullAddress)
                          }
                        >
                          <ArrowForwardIcon className={classes.proceedIcon} />
                          {t("root:proceed")}
                        </Fab>
                      </Grid>
                    </FormControl>
                  </Grid>
                  <CalendarTimeSlotSelector
                    dialogOpen={openSlotPicker}
                    bookingType={vehicleSlotSelection.testDriveType}
                    loading={availabilityLoading}
                    vehicleAvailabilityDropOffSlots={
                      vehicleAvailabilityDropOffSlots || []
                    }
                    vehicleAvailabilityPickUpSlots={
                      vehicleAvailabilityPickUpSlots || []
                    }
                    handleDialogState={handleSlotPickerClose}
                    fetchAvailability={(queryDateString: string) => {
                      getTestDriveAvailablilty({
                        variables: {
                          availabilityParams: {
                            dropoffQueryDate: d
                              .fromISO(queryDateString)
                              .plus({ day: 1 })
                              .toUTC()
                              .toISO(),
                            pickupQueryDate: d
                              .fromISO(queryDateString)
                              .toUTC()
                              .toISO(),
                            vehicleIds:
                              testDriveBookingState.availableVehicleIds,
                            branchId: testDriveBookingState.branchId,
                            category: BookingType.TEST_DRIVE,
                            pickupServiceLocation:
                              testDriveBookingState.pickupLocation,
                            dropoffOtherLocation: transformAddress(testDriveBookingState.pickupOtherLocation),
                            extendedTestDriveDuration:
                              formikProps.values.extendedTestDriveDuration,
                          },
                        },
                      });
                    }}
                    updateSlot={(data) => setSlot(data)}
                    updateSelectedDate={(val) =>
                      setVehicleSelection({
                        ...vehicleSlotSelection,
                        selectedDate: val,
                      })
                    }
                    setPickupSlotData={(
                      driverId: string,
                      slotId: string,
                      slotTime: string
                    ) => {
                      const newPickupTime = d
                        .fromISO(vehicleSlotSelection.selectedDate)
                        .startOf("day")
                        .plus({ hours: d.fromFormat(slotTime, "h:mm a").hour });
                      let newDropoffTime = newPickupTime;
                      if (isExtendedTestDrive && extendedTestDriveMaxHours) {
                        newDropoffTime = newPickupTime.plus({
                          hours: extendedTestDriveMaxHours,
                        });
                      } else {
                        newDropoffTime = newPickupTime.plus({
                          hours: vehicleSlotSelection.extendedTestDriveDuration,
                        });
                      }
                      setPickupDate(newPickupTime.toUTC().toISO());
                      setDropoffDate(newDropoffTime.toUTC().toISO());
                      formikProps.setValues({
                        ...formikProps.values,
                        pickupSlot: {
                          driverId,
                          slotId,
                        },
                      });
                    }}
                    setDropOffSlotData={(
                      driverId: string,
                      slotId: string,
                      slotTime: string
                    ) => {
                      const newPickupTime = d
                        .fromISO(vehicleSlotSelection.selectedDate, {
                          zone: currentBranch?.timeZone,
                        })
                        .startOf("day")
                        .plus({ hours: d.fromFormat(slotTime, "h:mm a").hour });
                      let newDropoffTime = newPickupTime;
                      if (isExtendedTestDrive && extendedTestDriveMaxHours) {
                        newDropoffTime = newPickupTime.plus({
                          hours: extendedTestDriveMaxHours,
                        });
                      } else if (
                        standardTestDriveDuration &&
                        standardTestDriveDuration.active
                      ) {
                        newDropoffTime = newDropoffTime.plus({
                          hours: standardTestDriveDuration.hour,
                        });
                      } else {
                        newDropoffTime = newPickupTime.plus({
                          hours:
                            vehicleSlotSelection.extendedTestDriveDuration || 1,
                        });
                      }
                      setPickupDate(newPickupTime.toUTC().toISO());
                      setDropoffDate(newDropoffTime.toUTC().toISO());

                      formikProps.setValues({
                        ...formikProps.values,
                        dropoffSlot: {
                          driverId,
                          slotId,
                        },
                      });
                    }}
                  />
                </Form>
              )}
            </Formik>
          </Grid>
        </Grid>
      </Grid>
    </Grid>
  );
};
